/**
 * Hijri date calculation tuned for the Omani official calendar (Um Al-Qura base
 * with a +1 day adjustment that matches Oman's moon-sighting practice).
 *
 * The algorithmic core is the Gregorian→Fixed→Hijri tabular conversion used by
 * the Omani Ministry of Awqaf website (the same approach as the script above).
 * The OMANI_BASE_OFFSET (+1) reproduces the official Omani date for the current
 * epoch; users can add a personal ±2 day offset on top of it if their region
 * sights the moon a day earlier or later.
 */

export interface HijriDateResult {
  readonly day: number;
  readonly month: number;
  readonly year: number;
  readonly monthNameAr: string;
  readonly monthNameEn: string;
  readonly formatted: string;       // full Arabic string
  readonly formattedEn: string;     // full English string
}

const HIJRI_MONTH_AR = [
  "محرم", "صفر", "ربيع الأول", "ربيع الثاني",
  "جمادى الأولى", "جمادى الثانية", "رجب", "شعبان",
  "رمضان", "شوال", "ذو القعدة", "ذو الحجة"
] as const;

const HIJRI_MONTH_EN = [
  "Muharram", "Safar", "Rabi' al-Awwal", "Rabi' al-Thani",
  "Jumada al-Ula", "Jumada al-Thani", "Rajab", "Sha'ban",
  "Ramadan", "Shawwal", "Dhul Qi'dah", "Dhul Hijjah"
] as const;

/**
 * Oman officially follows moon sighting.  Empirically the tabular algorithm
 * produces a result that is 1 day AHEAD of the official Omani calendar for
 * the current epoch (2020s), so we subtract 1 to correct it.
 * Verified against: May 13 2026 = 25 Dhul Qadah 1447 on mara.gov.om.
 */
const OMANI_BASE_OFFSET = -1;

function isGregLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function gregToFixed(year: number, month: number, day: number): number {
  const a = Math.floor((year - 1) / 4);
  const b = Math.floor((year - 1) / 100);
  const c = Math.floor((year - 1) / 400);
  const d = Math.floor((367 * month - 362) / 12);
  let e = 0;
  if (month > 2) e = isGregLeapYear(year) ? -1 : -2;
  return 365 * (year - 1) + a - b + c + d + e + day;
}

function fixedToHijri(fixed: number): { year: number; month: number; day: number } {
  const EPOCH = 227015;
  const year = Math.floor((30 * (fixed - EPOCH) + 10646) / 10631);
  const yearStart = hijriYearStart(year);
  const m = Math.ceil((fixed - 29 - yearStart) / 29.5) + 1;
  const month = Math.min(m, 12);
  const monthStart = hijriMonthStart(year, month);
  const day = fixed - monthStart + 1;
  return { year, month, day };
}

function hijriYearStart(year: number): number {
  const EPOCH = 227015;
  return (year - 1) * 354 + Math.floor((3 + 11 * year) / 30) + EPOCH;
}

function hijriMonthStart(year: number, month: number): number {
  return Math.ceil(29.5 * (month - 1)) + hijriYearStart(year);
}

/**
 * Convert a Gregorian Date to a Hijri date.
 * Always uses the Oman calendar date (Asia/Muscat, UTC+4) so the result
 * matches the MARA official calendar regardless of the browser's timezone.
 *
 * @param date      The moment to convert.
 * @param userOffset  Additional ±2 day correction chosen by the user for their
 *                  region's moon-sighting (on top of the Omani base offset).
 */
export function toHijri(date: Date, userOffset = 0): HijriDateResult {
  // Asia/Muscat is always UTC+4 (no DST).
  const omanDate = new Date(date.getTime() + 4 * 60 * 60 * 1000);
  const y = omanDate.getUTCFullYear();
  const m = omanDate.getUTCMonth() + 1;
  const d = omanDate.getUTCDate();

  const fixed = gregToFixed(y, m, d) + OMANI_BASE_OFFSET + userOffset;
  const { year, month, day } = fixedToHijri(fixed);

  const idx = Math.max(0, Math.min(11, month - 1));
  const monthNameAr = HIJRI_MONTH_AR[idx] as string;
  const monthNameEn = HIJRI_MONTH_EN[idx] as string;

  return {
    day,
    month,
    year,
    monthNameAr,
    monthNameEn,
    formatted: `${day} ${monthNameAr} ${year} هـ`,
    formattedEn: `${day} ${monthNameEn} ${year} AH`
  };
}
